import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Alert, Box, Button, Card, CardContent, Skeleton, Stack, Typography } from "@mui/material";
import { EmptyState, Icon, PageHeader, StatusChip, formatDateEs, type IconName } from "@despejo/ui";
import { ObligationCard } from "../components/ObligationCard.js";
import { ObligationDialog } from "../components/ObligationDialog.js";
import { buildDashboard, haciendaSummary, type DashboardItem } from "../lib/dashboard.js";
import { useInvalidateOrg, useObligations, useStatuses, useTaxpayers } from "../lib/queries.js";
import { useQuery } from "@tanstack/react-query";
import { must, supabase } from "../lib/supabase.js";
import { DeadlineText } from "@despejo/ui";
import type { ObligationRow } from "../lib/types.js";

const LONG_DATE = new Intl.DateTimeFormat("es-CR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/Costa_Rica" });
const TIME = new Intl.DateTimeFormat("es-CR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Costa_Rica" });

/** Overdue backlog per taxpayer (plan §12.1): a line each, linking to the taxpayer's catch-up action. */
function OverdueSummary({ items }: { items: DashboardItem[] }) {
  const by = new Map<string, { name: string; n: number; oldest: string }>();
  for (const i of items) {
    const cur = by.get(i.row.taxpayer_id);
    by.set(i.row.taxpayer_id, { name: i.row.taxpayer_name, n: (cur?.n ?? 0) + 1, oldest: cur && cur.oldest < i.row.due_date ? cur.oldest : i.row.due_date });
  }
  return (
    <Stack spacing={1}>
      {[...by.entries()].map(([id, v]) => (
        <Card key={id} component={Link} to={`/contribuyentes/${id}?tab=obligaciones`} sx={{ textDecoration: "none" }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="body2">
              {v.name}: {v.n} {v.n === 1 ? "obligación vencida" : "obligaciones vencidas"} desde {formatDateEs(v.oldest)}. Si ya las presentó fuera de Despejo, póngalas al día en un paso.
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

const SECTION_LIMIT = 5;

function SectionBlock({ title, icon, items, tone, onOpen }: { title: string; icon: IconName; items: DashboardItem[]; tone?: "error"; onOpen: (r: ObligationRow) => void }) {
  if (items.length === 0) return null;
  const limited = tone === "error" && items.length > SECTION_LIMIT;
  return (
    <Box component="section" aria-label={title}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5, color: tone === "error" ? "error.main" : "text.primary" }}>
        <Icon name={icon} fill={tone === "error"} />
        <Typography variant="overline" component="h2" sx={{ fontSize: 13 }}>
          {title} · {items.length}
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        {(limited ? items.slice(0, SECTION_LIMIT) : items).map((i) => (
          <ObligationCard key={i.row.id} row={i.row} onOpen={onOpen} />
        ))}
        {limited && <OverdueSummary items={items} />}
      </Stack>
    </Box>
  );
}

export function DashboardPage({ org }: { org: string }) {
  const obligations = useObligations(org);
  const statuses = useStatuses(org);
  const taxpayers = useTaxpayers(org);
  const invalidate = useInvalidateOrg(org);
  const [open, setOpen] = useState<ObligationRow | null>(null);
  const now = useMemo(() => new Date(), [obligations.dataUpdatedAt]);

  const dash = useMemo(() => buildDashboard(obligations.data ?? [], now), [obligations.data, now]);
  const acceptance = useQuery({
    queryKey: ["acceptance-queue", org],
    queryFn: async () =>
      must(await supabase.from("electronic_receipts").select("taxpayer_id, acceptance_due_date").eq("organization_id", org).eq("acceptance_status", "PENDING").order("acceptance_due_date")) as { taxpayer_id: string; acceptance_due_date: string }[],
  });
  const acceptByTp = useMemo(() => {
    const m = new Map<string, { count: number; earliest: string }>();
    for (const r of acceptance.data ?? []) {
      const cur = m.get(r.taxpayer_id);
      m.set(r.taxpayer_id, { count: (cur?.count ?? 0) + 1, earliest: cur?.earliest ?? r.acceptance_due_date });
    }
    return [...m.entries()];
  }, [acceptance.data]);
  const hacienda = haciendaSummary(statuses.data ?? [], (taxpayers.data ?? []).map((t) => t.id));
  const names = new Map((taxpayers.data ?? []).map((t) => [t.id, t.display_name]));

  if (obligations.isLoading || taxpayers.isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="text" width={240} height={48} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={96} />
        ))}
      </Stack>
    );
  }
  if (obligations.error) return <Alert severity="error">No se pudieron cargar las obligaciones: {(obligations.error as Error).message}</Alert>;

  if ((taxpayers.data ?? []).length === 0) {
    return (
      <EmptyState
        icon="badge"
        title="Agregue su primer contribuyente"
        body="Con la cédula consultamos a Hacienda su régimen, actividades y estado, y generamos su calendario de obligaciones."
        action={
          <Button component={Link} to="/contribuyentes?nuevo=1" variant="contained" startIcon={<Icon name="add" />}>
            Agregar contribuyente
          </Button>
        }
      />
    );
  }

  const nothing = dash.counts.overdue + dash.counts.thisWeek + dash.counts.upcoming === 0;
  return (
    <>
      <PageHeader title="Inicio" subtitle={LONG_DATE.format(now)} />
      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}>
              <Box>
                <Typography variant="overline" component="h2">
                  Estado en Hacienda
                </Typography>
                {hacienda.kind === "ALL_CLEAR" && (
                  <Typography variant="body2">
                    {names.size} {names.size === 1 ? "contribuyente" : "contribuyentes"}: moroso NO · omiso NO (verificado {TIME.format(new Date(hacienda.checkedAt))})
                  </Typography>
                )}
                {hacienda.kind === "PROBLEM" && (
                  <Typography variant="body2" color="error">
                    Hacienda reporta morosidad u omisión para: {hacienda.taxpayerIds.map((id) => names.get(id)).join(", ")}
                  </Typography>
                )}
                {hacienda.kind === "UNKNOWN" && <Typography variant="body2">Aún no hay una verificación completa de todos los contribuyentes.</Typography>}
              </Box>
              {hacienda.kind === "ALL_CLEAR" && <StatusChip tone="success" icon="verified" label="Al día" size="medium" />}
              {hacienda.kind === "PROBLEM" && <StatusChip tone="error" icon="gpp_bad" label="Revisar" size="medium" />}
              {hacienda.kind === "UNKNOWN" && <StatusChip tone="neutral" icon="hourglass_empty" label="Pendiente" size="medium" />}
            </Stack>
          </CardContent>
        </Card>

        {acceptByTp.length > 0 && (
          <Box component="section" aria-label="Aceptación de facturas">
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5 }}>
              <Icon name="inbox" />
              <Typography variant="overline" component="h2" sx={{ fontSize: 13 }}>
                Facturas por aceptar
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {acceptByTp.map(([tp, v]) => (
                <Card key={tp} component={Link} to={`/contribuyentes/${tp}`} sx={{ textDecoration: "none" }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2">
                      {names.get(tp)}: {v.count} {v.count === 1 ? "factura recibida" : "facturas recibidas"} sin aceptar. Sin aceptación a tiempo el IVA no es acreditable.
                    </Typography>
                    <DeadlineText date={v.earliest} now={now} overdue={v.earliest < now.toISOString().slice(0, 10)} />
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}
        {nothing && <EmptyState icon="task_alt" title="Nada pendiente en los próximos 45 días" body="Las obligaciones siguientes aparecen en el calendario." />}
        <SectionBlock title="Vencidas" icon="error" tone="error" items={dash.overdue} onOpen={setOpen} />
        <SectionBlock title="Esta semana" icon="event_upcoming" items={dash.thisWeek} onOpen={setOpen} />
        <SectionBlock title="Próximas" icon="calendar_month" items={dash.upcoming} onOpen={setOpen} />
      </Stack>
      <ObligationDialog row={open} onClose={() => setOpen(null)} onChanged={() => void invalidate()} />
    </>
  );
}
